// Display helpers shared by the Balance Sheet screen and its exports.
// Formatting only — no financial calculation lives here.

export const COLOR = {
  green: "#4CAF50",
  greenDark: "#2e7d32",
  greenBg: "#e8f5e9",
  red: "#e53935",
  redDark: "#c62828",
  redBg: "#fdecea",
  blue: "#3f51b5",
  blueBg: "#eef0fb",
  slate: "#546e7a",
  slateDark: "#37474f",
  slateBg: "#eceff1",
  purple: "#7b1fa2",
  purpleDark: "#4a148c",
  purpleBg: "#f3e5f5",
  headerBg: "#f5f6f8",
  neutralBg: "#fafafa",
};

// Sign handled inside fmt, so nothing else needs to prepend "-".
export const fmt = (n: number) => {
  const abs = Math.abs(n);
  const val = `$${abs.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return n < 0 ? `-${val}` : val;
};

// One rule for every balance figure: negative -> red, zero-or-positive -> green.
export const balanceColor = (n: number) => (n < 0 ? COLOR.redDark : COLOR.greenDark);

// ---------------------------------------------------------------------------
// Timezone-safe date math. "Today" is read out of APP_TIMEZONE once, as plain
// integers, and all month arithmetic is integer math — never `new Date(str)`,
// which parses date-only strings as UTC midnight and can shift by a day.
// ---------------------------------------------------------------------------

export const APP_TIMEZONE = "America/Toronto";

export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const pad2 = (n: number) => String(n).padStart(2, "0");

/** "YYYY-MM-DD" from plain integer parts (month is 1-12). */
export const ymd = (year: number, month: number, day: number) => `${year}-${pad2(month)}-${pad2(day)}`;

export const daysInMonth = (year: number, month: number) => new Date(year, month, 0).getDate();

/** Today in APP_TIMEZONE, month 1-12. */
export const getTodayInTZ = (): { year: number; month: number; day: number } => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: APP_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const map: Record<string, string> = {};
  parts.forEach((p) => {
    if (p.type !== "literal") map[p.type] = p.value;
  });
  return { year: Number(map.year), month: Number(map.month), day: Number(map.day) };
};

export const todayYmd = () => {
  const { year, month, day } = getTodayInTZ();
  return ymd(year, month, day);
};

/** Shift a (year, month 1-12) pair by whole months. */
export const addMonths = (year: number, month: number, delta: number) => {
  const index = year * 12 + (month - 1) + delta;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
};
